import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { 
  Bell, CheckCheck, Swords, Trophy, Sparkles, 
  ExternalLink, Trash2, ShieldCheck, ChevronRight 
} from 'lucide-react';
import './NotificationBell.css';

function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const now = new Date();
  const date = new Date(dateString);
  const diffSec = Math.floor((now - date) / 1000);

  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function getNotificationIcon(type) {
  switch (type) {
    case 'challenge':
      return <Swords size={16} className="text-amber-400" />;
    case 'tournament':
      return <Trophy size={16} className="text-yellow-400" />;
    case 'daily_puzzle':
      return <Sparkles size={16} className="text-blue-400" />;
    case 'broadcast':
      return <Bell size={16} className="text-purple-400" />;
    default:
      return <ShieldCheck size={16} className="text-emerald-400" />;
  }
}

export default function NotificationBell({ isCollapsed = false, onDropdownToggle = null }) {
  const {
    notifications,
    unreadCount,
    permission,
    isSupported,
    enableWebPush,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotification();

  const [isOpen, setIsOpen] = useState(false);
  const [enablingPush, setEnablingPush] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  const handleToggle = () => {
    const nextState = !isOpen;
    setIsOpen(nextState);
    if (onDropdownToggle) {
      onDropdownToggle(nextState);
    }
  };

  const handleNotificationClick = (item) => {
    if (!item.is_read) {
      markAsRead(item.id);
    }
    setIsOpen(false);
    if (item.data && item.data.url) {
      if (item.data.url.startsWith('http')) {
        window.open(item.data.url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(item.data.url);
      }
    }
  };

  const handleEnablePush = async (e) => {
    e.stopPropagation();
    try {
      setEnablingPush(true);
      await enableWebPush();
    } catch (err) {
      console.warn('Could not enable push:', err);
    } finally {
      setEnablingPush(false);
    }
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        className={`notification-bell-trigger ${isOpen ? 'active' : ''}`}
        onClick={handleToggle}
        title="Notifications"
        aria-label="Open notifications"
      >
        <Bell size={20} className="bell-icon" />
        {unreadCount > 0 && (
          <span className="unread-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown-panel">
          {/* Header */}
          <div className="notification-dropdown-header">
            <div className="flex items-center gap-2">
              <span className="dropdown-title">Notifications</span>
              {unreadCount > 0 && (
                <span className="unread-count-pill">{unreadCount} new</span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={markAllAsRead}
                title="Mark all as read"
              >
                <CheckCheck size={14} />
                <span>Mark all read</span>
              </button>
            )}
          </div>

          {/* Web Push Opt-in Nudge Banner */}
          {isSupported && permission === 'default' && (
            <div className="push-nudge-banner">
              <div className="push-nudge-text">
                <span className="nudge-title">Enable Push Notifications</span>
                <span className="nudge-desc">Get instant desktop alerts for challenges</span>
              </div>
              <button
                className="push-nudge-btn"
                onClick={handleEnablePush}
                disabled={enablingPush}
              >
                {enablingPush ? 'Enabling...' : 'Enable'}
              </button>
            </div>
          )}

          {/* Notifications List */}
          <div className="notification-dropdown-list">
            {notifications.length === 0 ? (
              <div className="notification-empty-state">
                <div className="empty-icon-wrap">
                  <Bell size={28} className="text-zinc-600" />
                </div>
                <p className="empty-text">No notifications yet</p>
                <span className="empty-subtext">You are all caught up! ♟️</span>
              </div>
            ) : (
              notifications.map((item) => (
                <div
                  key={item.id}
                  className={`notification-item ${!item.is_read ? 'unread' : 'read'}`}
                  onClick={() => handleNotificationClick(item)}
                >
                  <div className="item-icon-col">
                    {getNotificationIcon(item.type)}
                  </div>

                  <div className="item-content-col">
                    <div className="item-header-row">
                      <span className="item-title">{item.title}</span>
                      <span className="item-time">{formatRelativeTime(item.created_at)}</span>
                    </div>
                    <p className="item-body">{item.body}</p>
                    {item.data?.url && (
                      <span className="item-action-hint">
                        <span>View</span>
                        <ChevronRight size={12} />
                      </span>
                    )}
                  </div>

                  <div className="item-actions-col" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="delete-item-btn"
                      onClick={() => deleteNotification(item.id)}
                      title="Delete"
                      aria-label="Delete notification"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
